import { useIntervalFn } from '@vueuse/core'
import { onMounted, ref, watch } from 'vue'
import type { Ref } from 'vue'
import { globalHelperFunctions } from '../../functions/global-functions'

export function useDateNowInterval<T>(
  onUpdate: () => void,
  dataWatcher?: {
    /** The data to watch for changes */
    data: Ref<T>
    /** The key to watch for changes in the data */
    key: string
  },
  options?: {
    /** The interval to update the date. */
    interval?: number
    /** If true, the interval will not be set to the lowest second of the current time. */
    noLifecycleHooks?: boolean
    /** If true, the interval will be set to the lowest second of the current time. */
    matchUsersTime?: boolean
  }
) {
  const dateNow = ref(new Date().getTime())

  function updateDateNow() {
    dateNow.value = new Date().getTime()
    onUpdate()
  }

  const { pause, resume } = useIntervalFn(
    updateDateNow,
    options?.interval ?? 60000,
    {
      immediate: false
    }
  )

  function getLowestSecond(val: T | undefined) {
    let lowestSecond = 60
    if (options?.matchUsersTime) {
      const now = new Date()
      now.setSeconds(0, 0)
      now.setMinutes(now.getMinutes() + 1)
      // In order for the interval to be correct, we need to add 1 minute to the current time and set the seconds to 0.
      return globalHelperFunctions.getTimeRemainingFromNow(now, false).seconds
    }
    if (Array.isArray(val)) {
      for (let i = 0; i < val.length; i++) {
        const element = val[i]
        const key = dataWatcher?.key
        if (key) {
          const { seconds } = globalHelperFunctions.getTimeRemainingFromNow(
            element[key],
            false
          )
          if (seconds < lowestSecond) {
            lowestSecond = seconds
          }
        }
      }
    }
    return lowestSecond
  }

  const startUpdaterTimeout = ref<ReturnType<typeof setTimeout>>()

  function resumeUpdater() {
    pause()
    clearTimeout(startUpdaterTimeout.value)
    const lowestSecond = getLowestSecond(dataWatcher?.data.value)
    startUpdaterTimeout.value = setTimeout(() => {
      updateDateNow()
      resume()
    }, lowestSecond * 1000)
  }

  watch(
    () => dataWatcher?.data.value,
    (val) => {
      pause()
      clearTimeout(startUpdaterTimeout.value)
      const lowestSecond = getLowestSecond(val)
      startUpdaterTimeout.value = setTimeout(() => {
        updateDateNow()
        resume()
      }, lowestSecond * 1000)
    },
    {
      deep: true
    }
  )

  if (!options?.noLifecycleHooks) {
    onMounted(() => {
      pause()
      clearTimeout(startUpdaterTimeout.value)
      const lowestSecond = getLowestSecond(dataWatcher?.data.value)
      startUpdaterTimeout.value = setTimeout(() => {
        updateDateNow()
        resume()
      }, lowestSecond * 1000)
    })
  }

  return {
    /** Returns the stored time value in milliseconds since midnight, January 1, 1970 UTC. */
    dateNow,
    pause,
    resume: resumeUpdater
  }
}
